import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Badge,
  Tooltip,
  InputNumber,
  Checkbox,
  Popconfirm,
  Empty,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  GoldOutlined,
} from "@ant-design/icons";
import { api } from "@/utils/apiService";
import ErrorBoundary from "../common/ErrorBoundary";
import moment from "moment";

const { Option } = Select;

// Admin VIP routes are mounted at /api/admin/vip, while apiService baseURL is /api/v1
const ADMIN_VIP_BASE = "/../admin/vip";

const VipManagement = () => {
  const [tiers, setTiers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isVipModalVisible, setIsVipModalVisible] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [vipForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("tiers");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tiersResponse, usersResponse] = await Promise.all([
        api.get("/vip/tiers"),
        api.get("/users"),
      ]);

      // Handle tiers response - ensure it's an array
      const tiersData = tiersResponse.data;
      const tiersArray = Array.isArray(tiersData)
        ? tiersData
        : tiersData?.tiers
          ? tiersData.tiers
          : tiersData?.data
            ? tiersData.data
            : [];
      setTiers(tiersArray);

      // Handle users response - ensure it's an array
      const usersData = usersResponse.data;
      const usersArray = Array.isArray(usersData)
        ? usersData
        : usersData?.users
          ? usersData.users
          : usersData?.data
            ? usersData.data
            : [];
      setUsers(usersArray);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error(
        "Failed to load data. Please check if the backend is running.",
      );
      // Set empty arrays on error to prevent filter errors
      setTiers([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTier = () => {
    setEditingTier(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    form.setFieldsValue({
      ...tier,
      enabled: tier.enabled !== false,
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editingTier) {
        await api.put(`${ADMIN_VIP_BASE}/${editingTier._id}`, values);
        toast.success("Tier updated successfully");
      } else {
        // Ensure key is uppercase and trim values to avoid validation errors
        const payload = {
          ...values,
          key: values.key?.trim().toUpperCase(),
          name: values.name?.trim(),
        };
        await api.post(ADMIN_VIP_BASE, payload);
        toast.success("Tier created successfully");
      }

      setIsModalVisible(false);
      fetchData();
    } catch (err) {
      console.error("Error saving tier:", err);
      const errorMsg =
        err.response?.data?.message || err.message || "Failed to save tier";
      toast.error(errorMsg);
    }
  };

  const handleGrantVip = (userId) => {
    const user = users.find((u) => u._id === userId);
    if (!user) return;

    setSelectedUser(user);
    vipForm.resetFields();
    setIsVipModalVisible(true);
  };

  const handleVipSubmit = async (values) => {
    try {
      const { tierId } = values;
      await api.post(`${ADMIN_VIP_BASE}/assign/${selectedUser._id}`, {
        tierId,
      });
      toast.success("VIP status updated successfully");
      setIsVipModalVisible(false);
      fetchData();
    } catch (err) {
      console.error("Error assigning VIP:", err);
      const errorMsg =
        err.response?.data?.message || err.message || "Failed to assign VIP";
      toast.error(errorMsg);
    }
  };

  const handleRevokeVip = async (userId) => {
    try {
      await api.post(`${ADMIN_VIP_BASE}/remove/${userId}`);
      toast.success("VIP status revoked successfully");
      fetchData();
    } catch (err) {
      console.error("Error removing VIP:", err);
      const errorMsg =
        err.response?.data?.message || err.message || "Failed to remove VIP";
      toast.error(errorMsg);
    }
  };

  const handleDeleteTier = async (tierId) => {
    try {
      await api.delete(`${ADMIN_VIP_BASE}/${tierId}`);
      toast.success("Tier deleted successfully");
      fetchData();
    } catch (err) {
      console.error("Error deleting tier:", err);
      const errorMsg =
        err.response?.data?.message || err.message || "Failed to delete tier";
      toast.error(errorMsg);
    }
  };

  const tierColumns = [
    {
      title: "Tier",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          <Tag
            color={record.badgeColor || "#8b5cf6"}
            style={{ color: "white" }}
          >
            <CrownOutlined />
          </Tag>
          <div>
            <div className="fw-bold">{text}</div>
            <small className="text-muted">{record.key}</small>
          </div>
        </Space>
      ),
    },
    {
      title: "Discount",
      dataIndex: "discountPercent",
      key: "discount",
      render: (percent) => <Tag color="green">{percent || 0}% OFF</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) =>
        text || <span className="text-muted">No description</span>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Badge
          status={record.enabled !== false ? "success" : "default"}
          text={record.enabled !== false ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditTier(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this tier?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteTier(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const userColumns = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <div>
          <div className="fw-bold d-flex align-items-center">
            <UserOutlined className="me-2 text-muted" />
            {record.name || record.email}
          </div>
          <div className="text-muted small">{record.email}</div>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag
          color={
            role === "admin" ? "red" : role === "reseller" ? "blue" : "default"
          }
        >
          {role?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "VIP Status",
      key: "vipStatus",
      render: (_, record) => {
        // Handle both vip object and null vip
        const vipInfo = record.vip;

        if (!vipInfo || vipInfo.tier === null || !vipInfo.isActive) {
          return <Tag color="default">Regular User</Tag>;
        }

        const tier = tiers.find((t) => t._id === vipInfo.tier);
        const isExpired =
          vipInfo.expiresAt && new Date(vipInfo.expiresAt) < new Date();

        return (
          <Space direction="vertical" size="small">
            <Tag color={isExpired ? "default" : tier?.badgeColor || "purple"}>
              <CrownOutlined /> {tier?.name || "VIP"}
            </Tag>
            {vipInfo.expiresAt && (
              <span
                className={`small ${isExpired ? "text-danger" : "text-success"}`}
              >
                {isExpired ? "Expired: " : "Expires: "}
                {moment(vipInfo.expiresAt).format("MMM DD, YYYY")}
              </span>
            )}
          </Space>
        );
      },
    },

    {
      title: "Status",
      key: "userStatus",
      render: (_, record) => (
        <Badge
          status={record.isActive ? "success" : "default"}
          text={record.isActive ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const hasVip = record.vip?.isActive && record.vip?.tier;
        const isAdmin = record.role === "admin";

        return (
          <Space>
            {!hasVip ? (
              <Button
                type="primary"
                size="small"
                icon={<CrownOutlined />}
                onClick={() => handleGrantVip(record._id)}
                disabled={isAdmin}
              >
                Grant VIP
              </Button>
            ) : (
              <Space>
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleGrantVip(record._id)}
                  disabled={isAdmin}
                >
                  Change
                </Button>
                <Popconfirm
                  title="Remove VIP?"
                  description="This will remove VIP status from the user."
                  onConfirm={() => handleRevokeVip(record._id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<CloseCircleOutlined />}
                    disabled={isAdmin}
                  >
                    Remove
                  </Button>
                </Popconfirm>
              </Space>
            )}
          </Space>
        );
      },
    },
  ];

  // Statistics
  const vipUsers = Array.isArray(users)
    ? users.filter((user) => user.vip?.isActive && user.vip?.tier)
    : [];
  const totalUsers = Array.isArray(users) ? users.length : 0;
  const activeTiers = Array.isArray(tiers)
    ? tiers.filter((tier) => tier.enabled !== false)
    : [];

  return (
    <ErrorBoundary>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1">
                  <CrownOutlined className="me-2 text-warning" />
                  VIP Management
                </h2>
                <p className="text-muted mb-0">
                  Manage VIP tiers and user subscriptions
                </p>
              </div>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={handleAddTier}
              >
                Add VIP Tier
              </Button>
            </div>

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} className="mb-4">
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Users"
                    value={totalUsers}
                    prefix={<TeamOutlined />}
                    valueStyle={{ color: "#3f8600" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="VIP Users"
                    value={vipUsers.length}
                    prefix={<CrownOutlined />}
                    valueStyle={{ color: "#cf1322" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="VIP Tiers"
                    value={activeTiers.length}
                    prefix={<GoldOutlined />}
                    valueStyle={{ color: "#722ed1" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="VIP Rate"
                    value={
                      totalUsers > 0
                        ? ((vipUsers.length / totalUsers) * 100).toFixed(1)
                        : 0
                    }
                    suffix="%"
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Tab Navigation */}
            <Card className="mb-4">
              <div className="mb-3">
                <Space size="large">
                  <Button
                    type={activeTab === "tiers" ? "primary" : "default"}
                    onClick={() => setActiveTab("tiers")}
                    icon={<GoldOutlined />}
                  >
                    VIP Tiers ({tiers.length})
                  </Button>
                  <Button
                    type={activeTab === "users" ? "primary" : "default"}
                    onClick={() => setActiveTab("users")}
                    icon={<TeamOutlined />}
                  >
                    All Users ({users.length})
                  </Button>
                  <Button
                    type={activeTab === "vip-users" ? "primary" : "default"}
                    onClick={() => setActiveTab("vip-users")}
                    icon={<CrownOutlined />}
                  >
                    VIP Users ({vipUsers.length})
                  </Button>
                </Space>
              </div>

              {/* Content based on active tab */}
              {activeTab === "tiers" && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">VIP Tiers</h5>
                  </div>
                  <Table
                    columns={tierColumns}
                    dataSource={tiers}
                    rowKey="_id"
                    loading={loading}
                    pagination={false}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="No VIP tiers found. Create your first tier to get started."
                        />
                      ),
                    }}
                  />
                </div>
              )}

              {activeTab === "users" && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">All Users</h5>
                  </div>
                  <Table
                    columns={userColumns}
                    dataSource={users}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} users`,
                    }}
                    scroll={{ x: true }}
                  />
                </div>
              )}

              {activeTab === "vip-users" && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">VIP Users</h5>
                  </div>
                  <Table
                    columns={userColumns}
                    dataSource={vipUsers}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} VIP users`,
                    }}
                    scroll={{ x: true }}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="No VIP users found. Grant VIP status to users to see them here."
                        />
                      ),
                    }}
                  />
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Tier Creation/Edit Modal */}
        <Modal
          title={
            <div className="d-flex align-items-center">
              <CrownOutlined className="me-2" />
              {editingTier ? "Edit VIP Tier" : "Create VIP Tier"}
            </div>
          }
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              discountPercent: 5,
              enabled: true,
              badgeColor: "#8b5cf6",
              unlockOrders: 0,
              unlockWindowDays: 0,
              freeValidityDays: 0,
              paidPriceInr: 0,
              paidValidityDays: 30,
              multiplier: 1,
              sortOrder: 1,
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Tier Unique Key"
                  name="key"
                  rules={[
                    { required: true, message: "Please enter a unique key" },
                  ]}
                >
                  <Input
                    placeholder="e.g., VIP1, GOLD"
                    disabled={!!editingTier}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Tier Name"
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a tier name" },
                  ]}
                >
                  <Input placeholder="e.g., Silver, Gold, Platinum" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Discount Percentage"
                  name="discountPercent"
                  rules={[
                    {
                      required: true,
                      message: "Please enter discount percentage",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: "100%" }}
                    addonAfter="%"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Badge Color" name="badgeColor">
                  <Input type="color" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Unlock Orders" name="unlockOrders">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Unlock Window (Days)" name="unlockWindowDays">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Free Validity (Days)" name="freeValidityDays">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Paid Price (INR)" name="paidPriceInr">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Paid Validity (Days)" name="paidValidityDays">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Multiplier" name="multiplier">
                  <InputNumber min={1} step={0.1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Badge Text" name="badgeText">
                  <Input placeholder="Text shown on badge" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Sort Order" name="sortOrder">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="enabled" valuePropName="checked">
                  <Checkbox>Active Tier</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Description" name="description">
              <Input.TextArea
                rows={3}
                placeholder="Describe the benefits of this VIP tier"
              />
            </Form.Item>

            <div className="d-flex justify-content-end">
              <Space>
                <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit">
                  {editingTier ? "Update Tier" : "Create Tier"}
                </Button>
              </Space>
            </div>
          </Form>
        </Modal>

        {/* VIP Assignment Modal */}
        <Modal
          title={
            <div className="d-flex align-items-center">
              <CrownOutlined className="me-2" />
              {`${selectedUser?.vip?.isActive ? "Change VIP Tier for" : "Grant VIP to"} ${selectedUser?.name || selectedUser?.email}`}
            </div>
          }
          open={isVipModalVisible}
          onCancel={() => setIsVipModalVisible(false)}
          footer={null}
          width={400}
        >
          <Form form={vipForm} layout="vertical" onFinish={handleVipSubmit}>
            <Form.Item
              label="Select VIP Tier"
              name="tierId"
              rules={[{ required: true, message: "Please select a VIP tier" }]}
            >
              <Select placeholder="Choose a VIP tier">
                {activeTiers.map((tier) => (
                  <Option key={tier._id} value={tier._id}>
                    <Space>
                      <Tag color={tier.badgeColor}>{tier.name}</Tag>(
                      {tier.discountPercent}% discount)
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <div className="d-flex justify-content-end">
              <Space>
                <Button onClick={() => setIsVipModalVisible(false)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {selectedUser?.vip?.isActive ? "Change Tier" : "Grant VIP"}
                </Button>
              </Space>
            </div>
          </Form>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default VipManagement;
